import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Save } from 'lucide-react';
import usePageContent from './usePageContent';
import PageHeroEditor from './PageHeroEditor';
import LivePreviewPanel, { ServicesPreview } from './LivePreviewPanel';

const defaults = {
  hero: {
    title: 'Services &',
    highlight: 'Benefits',
    description:
      'Discover the comprehensive support and benefits we provide to our members across Papua New Guinea.',
  },
  intro: {
    heading: 'How We Support Our Members',
    body_markdown:
      'PEA PNG provides practical support, representation, and services that help protect members\' rights and wellbeing.',
  },
  services: {
    items: [
      {
        title: 'Industrial Services',
        body_markdown:
          'Support with workplace matters, representation, and negotiations to improve terms and conditions.',
      },
      {
        title: 'Legal Services',
        body_markdown:
          'Access to guidance and representation for employment-related legal issues and disputes.',
      },
      {
        title: 'Welfare Services',
        body_markdown:
          'Member welfare support and assistance programs designed to strengthen wellbeing and solidarity.',
      },
    ],
  },
  cta: {
    heading: 'Need Support?',
    body_markdown: 'Contact us to learn more about services, membership, and representation.',
    button_text: 'Contact Us',
    button_link: '/contact',
  },
};

const ServicesContentEditor = () => {
  const [showPreview, setShowPreview] = useState(false);
  const { loading, saving, sections, setSections, saveSection } = usePageContent({
    pageName: 'services',
    defaultsBySection: defaults,
  });

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Live Preview Panel */}
      <LivePreviewPanel 
        pageName="services" 
        showPreview={showPreview} 
        setShowPreview={setShowPreview}
      >
        <ServicesPreview content={sections} />
      </LivePreviewPanel>

      <PageHeroEditor
        title={sections.hero.title}
        setTitle={(v) => setSections({ ...sections, hero: { ...sections.hero, title: v } })}
        highlight={sections.hero.highlight}
        setHighlight={(v) => setSections({ ...sections, hero: { ...sections.hero, highlight: v } })}
        description={sections.hero.description}
        setDescription={(v) => setSections({ ...sections, hero: { ...sections.hero, description: v } })}
        onSave={() => saveSection('hero')}
        saving={saving}
      />

      <Card>
        <CardHeader>
          <CardTitle>Intro</CardTitle>
          <CardDescription>Markdown supported</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Heading</Label>
            <Input
              value={sections.intro.heading}
              onChange={(e) => setSections({ ...sections, intro: { ...sections.intro, heading: e.target.value } })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Body (Markdown)</Label>
            <Textarea
              value={sections.intro.body_markdown}
              onChange={(e) => setSections({ ...sections, intro: { ...sections.intro, body_markdown: e.target.value } })}
              className="mt-1"
              rows={4}
            />
          </div>
          <Button
            type="button"
            onClick={() => saveSection('intro')}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} /> Save Intro
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Services</CardTitle>
          <CardDescription>3 items (Markdown supported)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(sections.services.items || []).slice(0, 3).map((item, idx) => (
            <div key={idx} className="border rounded-xl p-4 bg-gray-50 space-y-3">
              <p className="font-semibold">Service {idx + 1}</p>
              <div>
                <Label>Title</Label>
                <Input
                  value={item.title}
                  onChange={(e) => {
                    const next = [...sections.services.items];
                    next[idx] = { ...next[idx], title: e.target.value };
                    setSections({ ...sections, services: { ...sections.services, items: next } });
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Body (Markdown)</Label>
                <Textarea
                  value={item.body_markdown}
                  onChange={(e) => {
                    const next = [...sections.services.items];
                    next[idx] = { ...next[idx], body_markdown: e.target.value };
                    setSections({ ...sections, services: { ...sections.services, items: next } });
                  }}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
          ))}

          <Button
            type="button"
            onClick={() => saveSection('services')}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} /> Save Services
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>CTA</CardTitle>
          <CardDescription>Markdown supported</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Heading</Label>
            <Input
              value={sections.cta.heading}
              onChange={(e) => setSections({ ...sections, cta: { ...sections.cta, heading: e.target.value } })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Body (Markdown)</Label>
            <Textarea
              value={sections.cta.body_markdown}
              onChange={(e) => setSections({ ...sections, cta: { ...sections.cta, body_markdown: e.target.value } })}
              className="mt-1"
              rows={3}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Button Text</Label>
              <Input
                value={sections.cta.button_text}
                onChange={(e) => setSections({ ...sections, cta: { ...sections.cta, button_text: e.target.value } })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Button Link</Label>
              <Input
                value={sections.cta.button_link}
                onChange={(e) => setSections({ ...sections, cta: { ...sections.cta, button_link: e.target.value } })}
                className="mt-1"
              />
            </div>
          </div>

          <Button
            type="button"
            onClick={() => saveSection('cta')}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} /> Save CTA
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServicesContentEditor;
